import React, { useEffect, useMemo, useState } from 'react';
import { CheckIcon, PaperclipIcon, ScanLineIcon } from 'lucide-react';
import type { AdminRole } from '../../../types/config';
import { useStore } from '../../../contexts/StoreContext';
import { useCaseActions } from '../../../hooks/useCaseActions';
import { Modal } from '../../shared/Modal';
import { StatusBadge } from '../../shared/StatusBadge';
import { DOC_LABEL } from '../../../utils/caseStatus';
import { bn } from '../../../utils/bn';
interface Props {
  open: boolean;
  onClose: () => void;
  role: AdminRole;
  initialPcr?: string;
  onDone: (pcr: string) => void;
}
interface Row {
  received: boolean;
  original: boolean;
  file: string;
}

/** Records documents handed in at the divisional office (or assisted online) and links them to the PCR. */
export function OfficeSubmissionModal({
  open,
  onClose,
  role,
  initialPcr,
  onDone
}: Props) {
  const {
    cases
  } = useStore();
  const actions = useCaseActions(role);
  const eligible = useMemo(() => cases.filter(c => c.stage !== 'released' && c.stage !== 'registered' && !c.verification), [cases]);
  const [pcr, setPcr] = useState('');
  const [source, setSource] = useState<'office' | 'online'>('office');
  const [claimant, setClaimant] = useState('');
  const [claimantId, setClaimantId] = useState('');
  const [date, setDate] = useState('2026-10-15');
  const [remarks, setRemarks] = useState('');
  const [rows, setRows] = useState<Record<string, Row>>({});
  const [error, setError] = useState('');
  const c = cases.find(x => x.pcr === pcr);
  useEffect(() => {
    if (!open) return;
    const start = initialPcr && eligible.some(x => x.pcr === initialPcr) ? initialPcr : eligible[0]?.pcr ?? '';
    setPcr(start);
    setSource('office');
    setRemarks('');
    setError('');
    setDate('2026-10-15');
  }, [open, initialPcr, eligible]);
  useEffect(() => {
    if (!c) return;
    setClaimant(c.ownerName);
    setClaimantId('');
    setRows(Object.fromEntries(c.documents.map(d => [d.key, {
      received: d.status === 'none' || d.status === 'correction',
      original: false,
      file: ''
    }])));
  }, [pcr]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key: string, patch: Partial<Row>) => setRows(r => ({
    ...r,
    [key]: {
      ...r[key],
      ...patch
    }
  }));
  const selected = Object.entries(rows).filter(([, r]) => r.received);
  const submit = () => {
    if (!c) {
      setError('একটি PCR নির্বাচন করুন।');
      return;
    }
    if (!selected.length) {
      setError('অন্তত একটি গৃহীত কাগজ নির্বাচন করুন।');
      return;
    }
    const missing = selected.filter(([, r]) => !r.file);
    if (missing.length) {
      setError(`${bn(missing.length)}টি কাগজের স্ক্যান কপি সংযুক্ত হয়নি।`);
      return;
    }
    if (source === 'office' && !claimant.trim()) {
      setError('জমাদানকারীর নাম লিখুন।');
      return;
    }
    const originals = selected.filter(([, r]) => r.original).length;
    const note = [source === 'office' ? `জমাদানকারী: ${claimant}${claimantId ? ` (পরিচয়পত্র: ${claimantId})` : ''}` : 'অনলাইন জমা — কর্মকর্তার সহায়তায় আপলোড', `মূল কপির সাথে মিলানো: ${bn(originals)}/${bn(selected.length)}`, remarks].filter(Boolean).join(' · ');
    actions.recordOfficeDocs(c.pcr, selected.map(([k]) => k), new Date(`${date}T11:00:00`).toISOString(), note, source, Object.fromEntries(selected.map(([k, r]) => [k, r.file])));
    onDone(c.pcr);
    onClose();
  };
  return <Modal open={open} onClose={onClose} title="কাগজপত্র জমা গ্রহণ (অফিস / সহায়তাকৃত)" size="lg" footer={<>
        <button className="btn btn-outline" onClick={onClose} data-id="element-390" data-mp="249rfiixbw4b5">বাতিল</button>
        <button className="btn btn-primary" onClick={submit} data-id="element-391" data-mp="24bu20sz9vfb4"><CheckIcon className="h-4 w-4" data-id="element-392" data-mp="2i9shk1pbxhs7" /> জমা লিপিবদ্ধ করুন</button>
      </>} data-id="element-389" data-mp="1uhv7og5fwztq">
      <div className="grid gap-4 md:grid-cols-2" data-id="element-393" data-mp="2dapc4g5ft9jp">
        <div data-id="element-394" data-mp="2daz4gadyfssk">
          <label className="label" htmlFor="os-pcr" data-id="element-395" data-mp="2d8cszzny0t52">PCR নম্বর</label>
          <select id="os-pcr" className="input" value={pcr} onChange={e => setPcr(e.target.value)} data-id="element-396" data-mp="nwaqw5tmo4sy">
            {eligible.map(x => <option key={x.pcr} value={x.pcr} data-id="element-397" data-mp="iy99rqfrifk4">{x.pcr} — {x.regNo}</option>)}
          </select>
          {c && <p className="mt-1 text-xs text-ink-muted" data-id="element-398" data-mp="kfoc2l96g54s">{c.ownerName} · {c.reason}</p>}
        </div>
        <fieldset data-id="element-399" data-mp="patz5nebfpug">
          <legend className="label" data-id="element-400" data-mp="pehoeacq9q3d">জমার উৎস</legend>
          <div className="flex gap-2" data-id="element-401" data-mp="ud2swlz0ivxm">
            {([['office', 'সরাসরি অফিসে জমা'], ['online', 'অনলাইনে জমা']] as const).map(([v, l]) => <label key={v} className={`flex h-11 flex-1 cursor-pointer items-center justify-center rounded-lg border text-sm font-semibold transition-colors duration-150 ${source === v ? 'border-navy bg-navy-50 text-navy' : 'border-line text-ink-muted'}`} data-id="element-402" data-mp="z8i0wxac9xm4">
                <input type="radio" className="sr-only" name="os-src" checked={source === v} onChange={() => setSource(v)} data-id="element-403" data-mp="bki53iy67h0o" />{l}
              </label>)}
          </div>
        </fieldset>
        {source === 'office' && <>
            <div data-id="element-404" data-mp="3k13ev1ymme4u">
              <label className="label" htmlFor="os-cl" data-id="element-405" data-mp="3k0ju7dhldbn4">জমাদানকারীর নাম</label>
              <input id="os-cl" className="input" value={claimant} onChange={e => setClaimant(e.target.value)} data-id="element-406" data-mp="215yv72kb26ab" />
            </div>
            <div data-id="element-407" data-mp="1x43u9z6adux2">
              <label className="label" htmlFor="os-id" data-id="element-408" data-mp="1x5op7pwijfvg">পরিচয়পত্র নম্বর (ঐচ্ছিক)</label>
              <input id="os-id" className="input" placeholder="জাতীয় পরিচয়পত্র / কর্তৃত্বপত্র" value={claimantId} onChange={e => setClaimantId(e.target.value)} data-id="element-409" data-mp="1s47pvytog1gz" />
            </div>
          </>}
        <div data-id="element-410" data-mp="1i7wfxfhk7npp">
          <label className="label" htmlFor="os-date" data-id="element-411" data-mp="1i8o1rylcu72s">জমার তারিখ</label>
          <input id="os-date" type="date" className="input" value={date} onChange={e => setDate(e.target.value)} data-id="element-412" data-mp="1dfbo2ji7rkot" />
        </div>
      </div>

      {c && <div className="mt-5" data-id="element-413" data-mp="14fokps5xc4t4">
          <p className="label" data-id="element-414" data-mp="14ghja5uqbo06">গৃহীত কাগজপত্র ও স্ক্যান কপি</p>
          <div className="divide-y divide-line rounded-lg border border-line" data-id="element-415" data-mp="14dvci8fnxcj0">
            {c.documents.map(d => {
          const r = rows[d.key];
          if (!r) return null;
          return <div key={d.key} className="grid items-center gap-2 px-3 py-2.5 md:grid-cols-[1fr_auto_auto_220px]" data-id="element-416" data-mp="ku665tgqlskg">
                  <label className="flex items-center gap-2 text-sm font-semibold text-ink" data-id="element-417" data-mp="pt9d5jga28v2">
                    <input type="checkbox" className="h-4 w-4 accent-navy" checked={r.received} onChange={e => set(d.key, {
                received: e.target.checked
              })} data-id="element-418" data-mp="btqah668jpq5" />
                    {d.name}{d.required && <span className="text-danger" data-id="element-419" data-mp="13knyvy1kko5t">*</span>}
                  </label>
                  <StatusBadge size="sm" {...DOC_LABEL[d.status]} data-id="element-420" data-mp="13mqnqeqzkb8y" />
                  <label className={`flex items-center gap-1.5 text-xs ${r.received ? 'text-ink' : 'text-ink-soft'}`} data-id="element-421" data-mp="18izaad7ij6q5">
                    <input type="checkbox" className="h-3.5 w-3.5 accent-bdgreen" disabled={!r.received} checked={r.original} onChange={e => set(d.key, {
                original: e.target.checked
              })} data-id="element-422" data-mp="upzt86s314gh" />
                    মূল কপি দেখা হয়েছে
                  </label>
                  <div className="flex items-center gap-1.5" data-id="element-423" data-mp="1rb0e7tz59n6f">
                    {r.file ? <span className="flex min-w-0 flex-1 items-center gap-1 truncate rounded bg-success-50 px-2 py-1 text-xs text-success" data-id="element-424" data-mp="3bzc93tzo1hcz"><PaperclipIcon className="h-3 w-3 shrink-0" data-id="element-425" data-mp="370j30mmpksmu" /><span className="truncate" data-id="element-426" data-mp="3lq76nuid43dq">{r.file}</span></span> : <label className={`btn btn-sm btn-outline flex-1 ${r.received ? '' : 'pointer-events-none opacity-50'}`} data-id="element-427" data-mp="3gr41ma2dtx5r">
                        ফাইল নির্বাচন
                        <input type="file" accept="image/*,application/pdf" className="sr-only" onChange={e => e.target.files?.[0] && set(d.key, {
                  file: e.target.files[0].name
                })} data-id="element-428" data-mp="2t1rvxk69k787" />
                      </label>}
                    <button type="button" className="btn btn-sm btn-ghost px-2" disabled={!r.received} title="ডেমো স্ক্যান" aria-label="ডেমো স্ক্যান" onClick={() => set(d.key, {
                file: `${d.key}_scan_${pcr.slice(-3)}.pdf`
              })} data-id="element-429" data-mp="2y0mcsgko7lpo">
                      <ScanLineIcon className="h-4 w-4" data-id="element-430" data-mp="db6obs8xc937" />
                    </button>
                  </div>
                </div>;
        })}
          </div>
          <p className="mt-1 text-[11px] text-ink-muted" data-id="element-431" data-mp="2bs783agg6gy0">স্ক্যানার সংযোগ সিমুলেটেড — স্ক্যান আইকনে ক্লিক করলে নমুনা স্ক্যান কপি সংযুক্ত হবে।</p>
        </div>}

      <div className="mt-4" data-id="element-432" data-mp="2gpz9qw6w9hfe">
        <label className="label" htmlFor="os-rm" data-id="element-433" data-mp="2li0hc4t4ajz3">কর্মকর্তার মন্তব্য</label>
        <textarea id="os-rm" rows={2} className="textarea" placeholder="যেমন: মালিক সশরীরে উপস্থিত হয়ে মূল কাগজপত্র প্রদর্শন করেছেন।" value={remarks} onChange={e => setRemarks(e.target.value)} data-id="element-434" data-mp="2lk3fxd8c5lff" />
      </div>
      {error && <p className="mt-3 rounded-lg bg-danger-50 px-3 py-2 text-sm text-danger-700" role="alert" data-id="element-435" data-mp="2zhs16m6beul0">{error}</p>}
    </Modal>;
}